import Text16 from '@sdk/components/ui/typography/Text16';
import Text14 from '@sdk/components/ui/typography/Text14';
import useAuth from '@sdk/authentication/useAuth';
import gettext from '@sdk/lang/gettext';
import Modal from '@sdk/components/ui/modal/Modal';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import { ShiftFeedbackRating } from '@sdk/timesheets/types/ShiftFeedback';
import dateToMonthDay from '@sdk/utils/dateFormat/dateToMonthDay';
import dateToWeekday from '@sdk/utils/dateFormat/dateToWeekday';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import TextareaAutoheight from '@sdk/components/forms/TextareaAutoheight';

import type { TimesheetsDashboardNotification } from '@web/timesheets/types/TimesheetsDashboardNotification';

import { ModalContainer } from './shiftFeedbackNotificationModal.styles';
import { useShiftFeedbackNotificationModal } from './shiftFeedbackNotificationModal.hooks';
import { ShiftFeedbackRatingTile } from './ShiftFeedbackRatingTile';

const ratingOptions = [
  ShiftFeedbackRating.poor,
  ShiftFeedbackRating.fair,
  ShiftFeedbackRating.good,
  ShiftFeedbackRating.great,
  ShiftFeedbackRating.excellent,
];

type Props = {
  notification: TimesheetsDashboardNotification;
  isVisible: boolean;
  close: () => void;
};

export const ShiftFeedbackNotificationModal = ({ notification, isVisible, close }: Props) => {
  const shift = notification.items[0];
  const shiftId = shift.id;
  const projection = shift.timesheetProjections[shift.timesheetProjections.length - 1];

  const { currentUser } = useAuth();
  const {
    onDismiss,
    ratingProvided,
    commentRef,
    feedbackComment,
    setFeedbackComment,
    updateFeedback,
    shiftFeedbackRef,
    onRatingClicked,
  } = useShiftFeedbackNotificationModal({
    isVisible,
    shiftId,
    projection,
    close,
  });

  const modalConfig = { hideClose: false, noDismiss: false };

  return (
    <Modal isVisible={isVisible} onDismiss={onDismiss} fixed config={modalConfig}>
      <ModalContainer>
        {ratingProvided ? (
          <Column alignItems="center">
            <Row mBottom={10}>
              <Text16 semiBold>{gettext('Thank you for the feedback!')}</Text16>
            </Row>
            <Row mBottom={20}>
              <Text14>{gettext('Tell us more about your selection.')}</Text14>
            </Row>
            <Row mBottom={15} style={{ width: '100%' }}>
              <TextareaAutoheight
                ref={commentRef}
                minRows={4}
                placeholder={gettext('Add your comment')}
                value={feedbackComment}
                onChange={({ target: { value } }): void => {
                  setFeedbackComment(value);
                }}
              />
            </Row>
            <Row mBottom={20}>
              <ProgressButton width={160} buttonType={ButtonType.primary} onClick={updateFeedback}>
                {gettext('Submit')}
              </ProgressButton>
            </Row>
          </Column>
        ) : (
          <Column alignItems="center">
            <Row mBottom={10}>
              <Text16 semiBold>{gettext('How was your shift?')}</Text16>
            </Row>
            <Column mBottom={20} alignItems="center">
              <Text14 themeColor={ThemeColorType.gray}>
                {shift.position.name} · {shift.location.name}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {dateToWeekday(projection.clockIn)} · {dateToMonthDay(projection.clockIn)}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {formatTime(projection.clockIn, currentUser.settings.usTimeFormat)} ·{' '}
                {formatTime(projection.clockOut, currentUser.settings.usTimeFormat)}
              </Text14>
            </Column>

            <Row justifyContent="space-between" style={{ width: '100%' }}>
              {ratingOptions.map((ratingOption) => (
                <ShiftFeedbackRatingTile
                  rating={ratingOption}
                  key={ratingOption}
                  onClick={() => void onRatingClicked(ratingOption)}
                />
              ))}
            </Row>
          </Column>
        )}
      </ModalContainer>
    </Modal>
  );
};
